import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingLibraryIcon,
  UsersIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  UserGroupIcon,
  TableCellsIcon,
  ScaleIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  MegaphoneIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../lib/api';
import toast from 'react-hot-toast';

interface OnboardingWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const institutionTypes = [
  {
    value: 'BANK',
    name: 'Bank',
    description: 'FDIC-regulated. Checks include Member FDIC, FDIC advertising rules, and FDIC exam framing.',
    icon: BuildingLibraryIcon,
    color: 'bg-blue-100 text-blue-600',
  },
  {
    value: 'CREDIT_UNION',
    name: 'Credit Union',
    description: 'NCUA-regulated. Checks include Federally Insured by NCUA, NCUA advertising rules, and NCUA exam framing.',
    icon: UsersIcon,
    color: 'bg-green-100 text-green-600',
  },
  {
    value: 'BOTH',
    name: 'Both / Agency',
    description: 'Compliance checks for both FDIC and NCUA regulations.',
    icon: BriefcaseIcon,
    color: 'bg-slate-100 text-slate-600',
  },
];

const features = [
  {
    icon: ShieldCheckIcon,
    title: 'Marketing Compliance',
    description: 'Upload ads, emails, and social posts for instant AI-powered compliance scanning with UDAAP, Reg Z, and FDIC/NCUA checks.',
  },
  {
    icon: TableCellsIcon,
    title: 'HMDA Validation',
    description: 'Upload LAR files, run 160+ CFPB edit checks, and get plain-English explanations with fix suggestions.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description: 'Monitor denial rates, pricing disparities, and geographic patterns with statistical analysis and risk assessments.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description: 'IFLEP-aligned regression models for denial, pricing, and steering with matched-pair comparisons.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Exam Management',
    description: 'Track exam readiness, manage findings and remediation, and generate one-click examiner document packages.',
  },
  {
    icon: MegaphoneIcon,
    title: 'Social Media Compliance',
    description: 'Paste social media posts for instant compliance scanning with enforcement cross-reference and platform-specific guidance.',
  },
  {
    icon: BuildingOfficeIcon,
    title: 'CRA Compliance',
    description: 'Assessment area analysis, lending distribution, community development tracking, and AI-powered gap analysis.',
  },
  {
    icon: DocumentTextIcon,
    title: 'Compliance Program',
    description: 'Calendar, policies, training, board reports, regulatory intelligence, and compliance health scoring.',
  },
];

export default function OnboardingWizard({ isOpen, onClose }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const updateMutation = useMutation({
    mutationFn: (institutionType: string) =>
      authApi.updateProfile({ institutionType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setStep(1);
    },
    onError: () => {
      toast.error('Failed to save institution type');
    },
  });

  const handleSaveType = () => {
    if (!selectedType) {
      toast.error('Please select an institution type');
      return;
    }
    updateMutation.mutate(selectedType);
  };

  const handleFinish = (destination: string) => {
    onClose();
    navigate(destination);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Progress dots */}
                <div className="flex justify-center mb-6">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full mx-1 transition-colors ${
                        i === step ? 'bg-ocean-600' : i < step ? 'bg-ocean-300' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Step 1: Institution Type */}
                {step === 0 && (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                      Welcome to Clarid!
                    </Dialog.Title>
                    <Dialog.Description className="text-gray-600 mb-6">
                      What type of institution do you work with? This configures your compliance
                      checks, exam framing, and regulatory output across the entire platform.
                    </Dialog.Description>

                    <div className="grid grid-cols-1 gap-4 mb-6">
                      {institutionTypes.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => setSelectedType(type.value)}
                          className={`relative flex items-start p-4 border-2 rounded-lg text-left transition-all ${
                            selectedType === type.value
                              ? 'border-ocean-500 bg-ocean-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${type.color}`}>
                            <type.icon className="w-6 h-6" />
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="text-lg font-semibold text-gray-900">{type.name}</h4>
                              {selectedType === type.value && (
                                <CheckCircleIcon className="w-5 h-5 text-ocean-600" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSaveType}
                        disabled={!selectedType || updateMutation.isPending}
                        className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {updateMutation.isPending ? 'Saving...' : 'Continue'}
                      </button>
                      <button
                        onClick={() => {
                          onClose();
                        }}
                        className="px-6 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Skip
                      </button>
                    </div>
                  </>
                )}

                {/* Step 2: Feature Overview */}
                {step === 1 && (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                      Here's what you can do
                    </Dialog.Title>
                    <Dialog.Description className="text-gray-600 mb-6">
                      Clarid is a compliance operating system with eight integrated modules. Here's what's available on your plan.
                    </Dialog.Description>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 max-h-[400px] overflow-y-auto">
                      {features.map((feature) => (
                        <div
                          key={feature.title}
                          className="p-4 rounded-lg border border-gray-200 bg-gray-50"
                        >
                          <div className="flex items-center mb-2">
                            <feature.icon className="w-5 h-5 text-ocean-600 mr-2" />
                            <h4 className="font-semibold text-navy-900">{feature.title}</h4>
                          </div>
                          <p className="text-sm text-gray-600">{feature.description}</p>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => setStep(2)}
                      className="w-full bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
                    >
                      Got it, let's go
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </button>
                  </>
                )}

                {/* Step 3: Get Started */}
                {step === 2 && (
                  <>
                    <div className="text-center mb-6">
                      <div className="mx-auto w-16 h-16 rounded-full bg-mint-100 flex items-center justify-center mb-4">
                        <CheckCircleIcon className="w-8 h-8 text-mint-600" />
                      </div>
                      <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                        You're all set!
                      </Dialog.Title>
                      <Dialog.Description className="text-gray-600">
                        Choose where you'd like to start.
                      </Dialog.Description>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={() => handleFinish('/upload')}
                        className="w-full flex items-center p-4 rounded-lg border-2 border-ocean-200 bg-ocean-50 hover:border-ocean-400 transition-colors text-left"
                      >
                        <ShieldCheckIcon className="w-8 h-8 text-ocean-600 mr-4 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-navy-900">Review a marketing piece</h4>
                          <p className="text-sm text-gray-600">Upload an ad or marketing material for instant compliance scanning</p>
                        </div>
                        <ArrowRightIcon className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
                      </button>

                      <button
                        onClick={() => handleFinish('/hmda')}
                        className="w-full flex items-center p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors text-left"
                      >
                        <TableCellsIcon className="w-8 h-8 text-gray-500 mr-4 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-navy-900">Upload HMDA data</h4>
                          <p className="text-sm text-gray-600">Validate your LAR file with 160+ edit checks and AI explanations</p>
                        </div>
                        <ArrowRightIcon className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
                      </button>

                      <button
                        onClick={() => handleFinish('/teams')}
                        className="w-full flex items-center p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors text-left"
                      >
                        <UserGroupIcon className="w-8 h-8 text-gray-500 mr-4 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-navy-900">Set up your team</h4>
                          <p className="text-sm text-gray-600">Invite compliance officers and marketing team members</p>
                        </div>
                        <ArrowRightIcon className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
                      </button>

                      <button
                        onClick={() => handleFinish('/dashboard')}
                        className="w-full flex items-center p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors text-left"
                      >
                        <DocumentTextIcon className="w-8 h-8 text-gray-500 mr-4 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-navy-900">Explore the dashboard</h4>
                          <p className="text-sm text-gray-600">See your compliance health score and get familiar with Clarid</p>
                        </div>
                        <ArrowRightIcon className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
                      </button>
                    </div>
                  </>
                )}

                <p className="text-xs text-gray-500 mt-4 text-center">
                  You can change your institution type anytime in Settings
                </p>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
